import { observer } from "mobx-react-lite";
import { Grid, Typography, Button as MuiButton, CircularProgress } from "@mui/material";
import SmartAppStep from "../../components/apps/SmartAppStep";
import SmartAppBase from "../../components/apps/SmartAppBase";
import { Restore, OpenInNew, Cancel } from "@mui/icons-material";
import useThemeService from 'e.sic-platform.webclient/src/services/theme-service/ThemeService';
import { restoreDataApp } from "../../services/apps/SmartApps";
import { translationService } from "e.sic-platform.webclient/src/services/translation/TranslationService";
import { snackbarNotificationsService } from "e.sic-platform.webclient/src/services/notifications/SnackbarNotificationsService";
import { uploadFilesService } from "e.sic-platform.webclient/src/services/upload/UploadFilesService";
import { FileExtended } from "e.sic-platform.webclient/src/services/upload/FileUploadTracker";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect } from "react";
import JSZip from "jszip";

export default observer(function RestoreDataPage() {
  const restoreDataService = restoreDataApp.services.restoreDataService!;
  const {themeClass} = useThemeService();
  const navigate = useNavigate();

  if (restoreDataService.isConverted) restoreDataApp.setStep(3);

  const isRestoring = restoreDataService.restoreRunning;
  const isRestored = restoreDataService.isConverted;
  const restoreFailed = restoreDataService.restoreFailed;

  // If the user navigates away mid-restore (unmount), abort the in-flight upload + restore
  // requests so the server (RequestAborted) stops processing and cleans up the partial restore.
  useEffect(() => {
    return () => {
      if (restoreDataService.restoreRunning) {
        restoreDataService.cancelRestore();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validateAndAddFiles = useCallback(async (files: Array<FileExtended>) => {
    if (files.length === 0) return;

    const file = files[0];
    try {
      const arrayBuffer = await file.file.arrayBuffer();
      const zip = await JSZip.loadAsync(arrayBuffer);

      const fileNames = Object.keys(zip.files);
      const hasMp4 = fileNames.some(name => name.toLowerCase().endsWith(".mp4"));
      const hasMetadata = fileNames.some(name => name === "metadata.json.esic");

      if (!hasMp4) {
        snackbarNotificationsService.showError("restoreEsicData.error.noVideo");
        return;
      }

      if (!hasMetadata) {
        snackbarNotificationsService.showError("restoreEsicData.error.noMetadata");
        return;
      }

      uploadFilesService.addFilesForUpload(files);
    } catch (error) {
      snackbarNotificationsService.showError("restoreEsicData.error.noMetadata");
    }
  }, []);

  return (
    <SmartAppBase
      appStorage={restoreDataApp}
      icon={{
        svg: Restore,
        textKey: "restoreEsicData.button.run",
        className: themeClass(["icon-custom"]),
      }}
      fileTypesAccepted="application/zip,.zip,application/x-zip-compressed"
      onDroppedFiles={validateAndAddFiles}
      hideCopyUrl
      freeActivity
      hideToolbar
      hideImage
      smallTitle
    >
      <SmartAppStep
        step={2}
        titleKey={restoreFailed ? "restoreEsicData.failed.title" : "restoreEsicData.restore.title"}
        descriptionKey={restoreFailed ? "restoreEsicData.failed.description" : "restoreEsicData.restore.description"}
        visible={isRestoring || restoreFailed}
        appStorage={restoreDataApp}
      >
        {isRestoring && (
          <Grid container justifyContent="center" sx={{ width: "100%", mt: 4, mb: 4 }} rowSpacing={2}>
            <Grid item xs={12} display="flex" justifyContent="center">
              <CircularProgress size={60} />
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="center">
              <Typography color="text.secondary">
                {translationService.translate("restoreEsicData.restore.inProgress")}
              </Typography>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="center">
              <MuiButton
                variant="outlined"
                color="inherit"
                size="large"
                startIcon={<Cancel />}
                onClick={() => restoreDataService.cancelRestore()}
                sx={{ borderRadius: 8, px: 5, py: 1.5, fontSize: "1rem", textTransform: "none" }}
              >
                {translationService.translate("restoreEsicData.cancel")}
              </MuiButton>
            </Grid>
          </Grid>
        )}
        {restoreFailed && (
          <Grid container justifyContent="center" sx={{ width: "100%", mt: 4, mb: 4 }} rowSpacing={2}>
            <Grid item xs={12} display="flex" justifyContent="center">
              <Typography variant="h6" color="error.main">
                {restoreDataService.message || translationService.translate("restoreEsicData.failed.message")}
              </Typography>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="center">
              <Typography color="text.secondary" sx={{ textAlign: "center", maxWidth: 480 }}>
                {translationService.translate("restoreEsicData.failed.hint")}
              </Typography>
            </Grid>
            <Grid item xs={12} display="flex" justifyContent="center">
              <MuiButton
                variant="contained"
                size="large"
                startIcon={<Restore />}
                onClick={() => restoreDataApp.new()}
                sx={{ borderRadius: 8, px: 5, py: 1.5, fontSize: "1rem", textTransform: "none" }}
              >
                {translationService.translate("restoreEsicData.tryAgain")}
              </MuiButton>
            </Grid>
          </Grid>
        )}
      </SmartAppStep>

      <SmartAppStep
        step={3}
        titleKey="restoreEsicData.restored.title"
        descriptionKey="restoreEsicData.restored.description"
        visible={isRestored}
        appStorage={restoreDataApp}
      >
        <Grid container justifyContent="center" sx={{ width: "100%", mt: 4, mb: 4 }} rowSpacing={2}>
          <Grid item xs={12} display="flex" justifyContent="center">
            <Typography variant="h6" color="success.main">
              {translationService.translate("restoreEsicData.restored.message")}
            </Typography>
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="center" gap={2} flexWrap="wrap">
            {restoreDataService.viewDataRoute && (
              <MuiButton
                variant="contained"
                size="large"
                startIcon={<OpenInNew />}
                onClick={() => navigate(restoreDataService.viewDataRoute)}
                sx={{ borderRadius: 8, px: 5, py: 1.5, fontSize: "1rem", textTransform: "none" }}
              >
                {translationService.translate("restoreEsicData.viewData")}
              </MuiButton>
            )}
            <MuiButton
              variant="outlined"
              size="large"
              startIcon={<Restore />}
              onClick={() => restoreDataApp.new()}
              sx={{ borderRadius: 8, px: 5, py: 1.5, fontSize: "1rem", textTransform: "none" }}
            >
              {translationService.translate("restoreEsicData.restoreAnother")}
            </MuiButton>
          </Grid>
        </Grid>
      </SmartAppStep>
    </SmartAppBase>
  )
});
